/**
 * WitnessPredicateMapPanel — each categorical witness, the ZK predicate a proof
 * establishes of it, and the privacy gain (RD-25 §2, §3).
 *
 * Data–code separation: the map lives in `data/witnessPredicateMap.json` and is
 * rendered here. A dataset test pins the JSON against this panel's expectations.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import type { MonographDataTableColumn } from "@csk/demo-ui";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import witnessMapData from "../data/witnessPredicateMap.json" with { type: "json" };

type WitnessRow = (typeof witnessMapData.entries)[number];

function accentFor(token: string): string {
  return panelTokens[token];
}

const WITNESS_COLUMNS: readonly MonographDataTableColumn<WitnessRow>[] = [
  {
    accessorKey: "theorem",
    header: "Theorem",
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[12.5px] font-bold text-[var(--zkp-bright)]">
          {row.original.theorem}
        </span>
        {PanelSurface.badge(
          row.original.generator,
          accentFor(row.original.accent),
        )}
      </div>
    ),
  },
  {
    accessorKey: "witness",
    header: "Categorical witness",
    cell: ({ row }) => (
      <span className="text-[12.5px] text-[var(--zkp-text)]">
        {row.original.witness}
      </span>
    ),
  },
  {
    accessorKey: "predicate",
    header: "ZK predicate proved",
    cell: ({ row }) => (
      <span className="font-mono text-[12px] text-[var(--zkp-witness)]">
        {row.original.predicate}
      </span>
    ),
  },
  {
    accessorKey: "encoding",
    header: "ZKP encoding",
    cell: ({ row }) => (
      <span className="text-[12px] text-[var(--zkp-muted)]">
        {row.original.encoding}
      </span>
    ),
  },
];

export function WitnessPredicateMapPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="Witness → ZK Predicate"
      subtitle="Each CSK theorem requires a specific categorical witness. A zero-knowledge proof establishes a predicate ABOUT that witness — that it exists — without disclosing it. Here is the full map."
      accentColor={panelTokens.witness}
    >
      <PanelSurface.SectionHeading>
        The compliance predicate
      </PanelSurface.SectionHeading>
      <PanelSurface.Callout
        title="A ZKP proves the truncation, never the element"
        tone="info"
        color={panelTokens.witness}
      >
        For each theorem <code className="font-mono">T</code> and governance
        statement <code className="font-mono">x</code>, compliance is{" "}
        <code className="font-mono">IsCompliant_T x = ∥ W(T, x) ∥</code> — the
        propositional truncation of the witness type. The ZKP proves a witness{" "}
        <em>exists</em>; it reveals no element of{" "}
        <code className="font-mono">W(T, x)</code>. That is the categorical form
        of "proof of knowledge without knowledge of proof."
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>
        The per-theorem map
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.DataTable
          ariaLabel="Witness type to zero-knowledge predicate map"
          data={witnessMapData.entries}
          columns={WITNESS_COLUMNS}
          getRowId={(row) => row.id}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        What each proof keeps private
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ContentList
          ariaLabel="Privacy gain per witness type"
          variant="check"
          items={witnessMapData.entries.map((entry) => ({
            id: entry.id,
            title: `${entry.theorem} — ${entry.generator}`,
            body: entry.privacyGain,
            color: accentFor(entry.accent),
          }))}
        />
      </PanelSurface.Card>

      <PanelSurface.Callout
        title="Four generators, four sub-circuits (ZKP-6)"
        tone="safe"
        color={panelTokens.accentGold}
      >
        Every theorem above sits under one of four forbidden generators — G1
        Authority, G2 Knowledge, G3 Time, G4 Quantity. The ZKP circuit for full
        CSK compliance decomposes into exactly four sub-circuits, one per
        generator: complete (the four cover all ten theorems) and non-redundant
        (no three can substitute for the fourth). That is ZKP-6, lifting the
        generator-minimality result to the ZKP layer.
      </PanelSurface.Callout>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-25 §3 Per-theorem ZKP encoding",
            path: "research/settled/RD-25-ZERO-KNOWLEDGE-COMPLIANCE-PROOFS.md",
          },
          {
            label: "GeneratorCircuitDecomposition.agda (ZKP-6)",
            path: "agda/Research/ZeroKnowledgeCompliance/GeneratorCircuitDecomposition.agda",
          },
          {
            label: "CanonicalAuthorityDAG.agda (PERMIT, ZKP-2)",
            path: "agda/Research/ZeroKnowledgeCompliance/CanonicalAuthorityDAG.agda",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
